import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { historyApi, getToken } from '../api/client';

const STORAGE_KEY = 'stock-search-history';
const MAX_HISTORY = 10;

export interface SearchHistoryItem {
  ticker: string;
  name: string;
  timestamp: number;
}

interface BackendHistoryItem {
  id: number;
  type: string;
  data: {
    ticker: string;
    name: string;
  };
  createdAt: string;
}

export function useSearchHistory() {
  const queryClient = useQueryClient();
  const token = getToken();
  const isAuthenticated = !!token;

  // 未登录时的 localStorage 状态
  const [localHistory, setLocalHistory] = useState<SearchHistoryItem[]>([]);

  // 后端历史查询（仅登录时启用）
  const { data: backendHistory, isLoading } = useQuery<BackendHistoryItem[]>({
    queryKey: ['history', 'STOCK_SEARCH'],
    queryFn: () => historyApi.getHistory('STOCK_SEARCH'),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 分钟
  });

  // 登录后自动清理一次存量重复数据
  const cleanupExecuted = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !cleanupExecuted.current) {
      cleanupExecuted.current = true;
      historyApi.cleanupDuplicates('STOCK_SEARCH').then((result) => {
        if (result.removed > 0) {
          queryClient.invalidateQueries({ queryKey: ['history', 'STOCK_SEARCH'] });
        }
      }).catch(() => {
        // 静默失败，不影响用户体验
      });
    }
  }, [isAuthenticated, queryClient]);

  // 添加记录 mutation
  const addMutation = useMutation({
    mutationFn: (item: { ticker: string; name: string }) =>
      historyApi.addHistory('STOCK_SEARCH', item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history', 'STOCK_SEARCH'] });
    },
  });

  // 删除记录 mutation
  const deleteMutation = useMutation({
    mutationFn: (historyId: number) => historyApi.deleteHistory(historyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history', 'STOCK_SEARCH'] });
    },
  });

  // 清空历史 mutation
  const clearMutation = useMutation({
    mutationFn: () => historyApi.clearHistory('STOCK_SEARCH'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history', 'STOCK_SEARCH'] });
    },
  });

  // 未登录时从 localStorage 加载历史记录
  useEffect(() => {
    if (!isAuthenticated) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          setLocalHistory(JSON.parse(stored));
        }
      } catch (e) {
        console.error('Failed to load search history:', e);
      }
    }
  }, [isAuthenticated]);

  // 转换后端历史为统一格式
  const history: SearchHistoryItem[] = isAuthenticated
    ? (Array.isArray(backendHistory) ? backendHistory : []).map(item => ({
        ticker: item.data.ticker,
        name: item.data.name,
        timestamp: new Date(item.createdAt).getTime(),
      }))
    : localHistory;

  // 添加搜索记录
  const addToHistory = useCallback(
    (ticker: string, name: string) => {
      if (isAuthenticated) {
        addMutation.mutate({ ticker, name });
      } else {
        // 未登录降级到 localStorage
        setLocalHistory((prev) => {
          const filtered = prev.filter((item) => item.ticker !== ticker);
          const newHistory = [
            { ticker, name, timestamp: Date.now() },
            ...filtered,
          ].slice(0, MAX_HISTORY);

          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
          } catch (e) {
            console.error('Failed to save search history:', e);
          }

          return newHistory;
        });
      }
    },
    [isAuthenticated, addMutation]
  );

  // 删除单条记录
  const removeFromHistory = useCallback(
    (ticker: string) => {
      if (isAuthenticated) {
        const item = backendHistory?.find((h) => h.data.ticker === ticker);
        if (item) {
          deleteMutation.mutate(item.id);
        }
      } else {
        setLocalHistory((prev) => {
          const newHistory = prev.filter((item) => item.ticker !== ticker);
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
          } catch (e) {
            console.error('Failed to update search history:', e);
          }
          return newHistory;
        });
      }
    },
    [isAuthenticated, backendHistory, deleteMutation]
  );

  // 清除历史记录
  const clearHistory = useCallback(() => {
    if (isAuthenticated) {
      clearMutation.mutate();
    } else {
      setLocalHistory([]);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.error('Failed to clear search history:', e);
      }
    }
  }, [isAuthenticated, clearMutation]);

  return {
    history,
    addToHistory,
    clearHistory,
    removeFromHistory,
    isLoading,
  };
}
